import React, { useState, useEffect } from "react";
import {
  useCreateProductMutation,
  useUpdateProductMutation,
} from "../../../../hooks/useAdminProductCatalog";
import type {
  BillingProduct,
  ProductType,
} from "../../../../types/adminBilling";

export interface ProductFormModalProps {
  product?: BillingProduct | null;
  brandId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export const ProductFormModal: React.FC<ProductFormModalProps> = ({
  product,
  brandId,
  onClose,
  onSuccess,
}) => {
  const isEditing = !!product;
  const isArchived = product?.status === "ARCHIVED";
  const isActive = product?.status === "ACTIVE";

  const [name, setName] = useState(product?.name ?? "");
  const [description, setDescription] = useState(product?.description ?? "");
  const [productType, setProductType] = useState<ProductType>(
    product?.productType ?? "LEAF"
  );
  const [packageStructureText, setPackageStructureText] = useState(
    product?.packageStructure
      ? JSON.stringify(product.packageStructure, null, 2)
      : ""
  );
  const [packageStructureError, setPackageStructureError] = useState<
    string | null
  >(null);

  const createMutation = useCreateProductMutation();
  const updateMutation = useUpdateProductMutation(
    product?.id ?? "",
    brandId
  );

  const isLoading = createMutation.isPending || updateMutation.isPending;

  // Escape-to-close
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  const validatePackageStructure = (text: string): boolean => {
    if (!text.trim()) {
      setPackageStructureError(null);
      return true;
    }
    try {
      JSON.parse(text);
      setPackageStructureError(null);
      return true;
    } catch {
      setPackageStructureError("Nieprawidłowy JSON");
      return false;
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    if (
      !isEditing ||
      (isEditing && product?.status === "DRAFT" && productType === "PACKAGE")
    ) {
      if (!validatePackageStructure(packageStructureText)) return;
    }

    let parsedPackageStructure: Record<string, unknown> | null | undefined =
      undefined;
    if (packageStructureText.trim()) {
      try {
        parsedPackageStructure = JSON.parse(packageStructureText);
      } catch {
        return;
      }
    } else if (isEditing && product?.packageStructure) {
      parsedPackageStructure = null; // Clear it
    }

    if (isEditing) {
      updateMutation.mutate(
        {
          name: name.trim(),
          description: description.trim() || null,
          ...(product?.status === "DRAFT" && parsedPackageStructure !== undefined
            ? { packageStructure: parsedPackageStructure }
            : {}),
        },
        {
          onSuccess: () => {
            onSuccess();
            onClose();
          },
        }
      );
    } else {
      createMutation.mutate(
        {
          name: name.trim(),
          description: description.trim() || null,
          productType,
          brandId,
        },
        {
          onSuccess: () => {
            onSuccess();
            onClose();
          },
        }
      );
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-neutral-900">
            {isEditing ? "Edytuj produkt" : "Nowy produkt"}
          </h3>
          <button
            onClick={onClose}
            aria-label="Zamknij"
            className="text-neutral-400 hover:text-neutral-600"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {isArchived && (
          <div className="px-6 py-3 bg-neutral-50 border-b border-neutral-200">
            <p className="text-sm text-neutral-600">
              Produkt jest zarchiwizowany — wszystkie pola są tylko do odczytu.
            </p>
          </div>
        )}

        {isActive && !isArchived && (
          <div className="px-6 py-3 bg-amber-50 border-b border-amber-200">
            <p className="text-sm text-amber-700">
              Zmiana nie wpływa na istniejące subskrypcje (chronione snapshotem).
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label htmlFor="product-name" className="block text-sm font-medium text-neutral-700 mb-1">
              Nazwa *
            </label>
            <input
              id="product-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isArchived}
              required
              maxLength={255}
              className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-neutral-50 disabled:text-neutral-500"
              placeholder="Nazwa produktu"
            />
          </div>

          <div>
            <label htmlFor="product-description" className="block text-sm font-medium text-neutral-700 mb-1">
              Opis
            </label>
            <textarea
              id="product-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={isArchived}
              rows={3}
              className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-neutral-50 disabled:text-neutral-500"
              placeholder="Opis produktu (opcjonalny)"
            />
          </div>

          {!isEditing && (
            <div>
              <label htmlFor="product-type" className="block text-sm font-medium text-neutral-700 mb-1">
                Typ produktu *
              </label>
              <select
                id="product-type"
                value={productType}
                onChange={(e) => setProductType(e.target.value as ProductType)}
                className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="LEAF">LEAF</option>
                <option value="PACKAGE">PACKAGE</option>
              </select>
            </div>
          )}

          {(isEditing
            ? product?.productType === "PACKAGE" && product?.status === "DRAFT"
            : productType === "PACKAGE") && (
            <div>
              <label htmlFor="product-package-structure" className="block text-sm font-medium text-neutral-700 mb-1">
                Struktura pakietu (JSON)
              </label>
              <textarea
                id="product-package-structure"
                value={packageStructureText}
                onChange={(e) => {
                  setPackageStructureText(e.target.value);
                  validatePackageStructure(e.target.value);
                }}
                disabled={isArchived}
                rows={6}
                className={`w-full border rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-neutral-50 disabled:text-neutral-500 ${
                  packageStructureError
                    ? "border-red-300"
                    : "border-neutral-300"
                }`}
                placeholder='{"sets": [], "products": []}'
              />
              {packageStructureError && (
                <p className="text-red-600 text-xs mt-1">
                  {packageStructureError}
                </p>
              )}
            </div>
          )}

          {!isArchived && (
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm text-neutral-600 hover:text-neutral-800 font-medium transition-colors"
              >
                Anuluj
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
              >
                {isLoading ? "Zapisywanie..." : "Zapisz"}
              </button>
            </div>
          )}

          {isArchived && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm text-neutral-600 hover:text-neutral-800 font-medium"
              >
                Zamknij
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};
